/**
 * Padel USA — Racket Finder (custom product finder, replaces the Lantern quiz app)
 *
 * Reads an inline JSON candidate pool (rendered by sections/product-finder_custom.liquid from the
 * racket collection's custom.* metafields) and runs a client-side weighted matching engine:
 * hard filters (budget, gender, heavy/head-heavy guard for non-competitive women, stock) +
 * soft-scored criteria (style, level, feel, arm-comfort, age, tennis background, current-racket
 * gripe→fix, freshness) → top-3 ranked matches with transparent "why" reason chips.
 *
 * Flow branches on level: beginners get the tennis-background questions; intermediate/advanced
 * players get the current-racket branch (what they play now + what they'd change).
 *
 * Zero network calls to compute results. Email capture posts to Klaviyo's public client API
 * (company/list ids come from section settings). Add-to-cart uses the native /cart/add.js.
 *
 * XSS note: every dynamic value rendered below passes through esc(); the only HTML literals are
 * this file's own static templates and inline SVG icons. No user-typed content is ever rendered.
 *
 * Dossier: custom-code-padelusa/features/product-finder.md
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Question flow                                                       */
  /* ------------------------------------------------------------------ */

  // Bump when the scoring logic/weights change — sent with quiz_completed so the agent's training
  // signal (and any eval set) can be attributed to the engine version that produced it. (Named
  // PF_* to avoid clashing with the content-hash ENGINE_VERSION the racket-brain generator emits.)
  const PF_ENGINE_VERSION = '2026-06-16';

  const ICONS = {
    shield:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6z"/></svg>',
    arrows:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 8l-4 4 4 4M17 8l4 4-4 4M3 12h18"/></svg>',
    bolt:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2L4 14h6l-1 8 9-12h-6z"/></svg>',
    compass:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M15 9l-2 5-4 1 2-5z"/></svg>',
    feather:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 4c-5 0-12 4-13 13l-3 3M7 17h6c4 0 7-6 7-13z"/></svg>',
    balance:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v16M5 8h14M7 20h10"/><path d="M5 8l-2 5h4zM19 8l-2 5h4z"/></svg>',
    hammer:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="5" width="10" height="6" rx="1"/><path d="M14 8h6M9 11v8"/></svg>',
    ball:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M5 5.5C8 8 8 16 5 18.5M19 5.5C16 8 16 16 19 18.5"/></svg>',
  };

  const QUESTIONS = [
    {
      id: 'level',
      title: 'What’s your level on court?',
      sub: 'Be honest — the right racket meets you where you are.',
      options: [
        { v: 'beginner', label: 'Just starting out', desc: 'New to padel, or play casually' },
        { v: 'intermediate', label: 'Improving fast', desc: 'Play regularly, building consistency' },
        { v: 'advanced', label: 'Competitive', desc: 'Tournaments, leagues, serious matches' },
      ],
    },
    {
      // Prior racket-sport background — only relevant for newcomers (asked right after level).
      // An established padel player has already found what feels natural, so they instead get the
      // current-racket branch later. Tennis steers head-heavier; the paddle/table sports much less so.
      id: 'prior_sport',
      title: 'Did you play a racket sport before?',
      sub: 'Old habits shape which racket feels natural in your hand.',
      showIf: (a) => a.level === 'beginner',
      options: [
        { v: 'none', label: 'No, padel’s my first', desc: 'Straight into padel' },
        { v: 'tennis', label: 'Tennis', desc: 'I play or played tennis' },
        { v: 'pball', label: 'Pickleball', desc: 'Came from the paddle court' },
        { v: 'other', label: 'Ping pong, badminton or other', desc: 'Another racket sport' },
      ],
    },
    {
      id: 'style',
      title: 'How do you win points?',
      sub: 'Your style decides the shape and balance that suit you.',
      options: [
        { v: 'defensive', label: 'The wall', desc: 'Patient at the back — force the error', icon: 'shield' },
        { v: 'allround', label: 'All-court', desc: 'Adapt to whatever the point needs', icon: 'arrows' },
        { v: 'aggressive', label: 'The finisher', desc: 'Net rushes, smashes and volleys', icon: 'bolt' },
        { v: 'notsure', label: 'Still figuring it out', desc: 'No fixed style yet', icon: 'compass' },
      ],
    },
    {
      id: 'feel',
      title: 'What should impact feel like?',
      sub: 'The core of the racket sets its touch — comfort vs raw feedback.',
      options: [
        { v: 'soft', label: 'Soft', desc: 'Plush and forgiving — max comfort', icon: 'feather' },
        { v: 'medium', label: 'Balanced', desc: 'Not too soft, not too firm', icon: 'balance' },
        { v: 'firm', label: 'Firm', desc: 'Solid and direct — pure feedback', icon: 'hammer' },
        { v: 'notsure', label: 'Not sure yet', desc: 'Pick for me', icon: 'compass' },
      ],
    },
    {
      // Experienced-player branch (intermediate/advanced): they already own a racket.
      // Phase C — let them search their EXACT current racket from our catalogue (type-ahead).
      // Critical: we don't carry every racket on the market, so "I don't see mine" is a
      // first-class skip → falls through to the manual shape picker below.
      id: 'current_racket',
      type: 'search',
      title: 'What do you play with now?',
      sub: 'Search your current racket — we’ll find you a genuine step up. Not carried here? Just skip.',
      skippable: true,
      skipLabel: 'I don’t see mine / not sure',
      showIf: (a) => a.level && a.level !== 'beginner',
    },
    {
      // Fallback shape picker — only shown if they didn't pick an exact racket above.
      id: 'current_shape',
      title: 'What’s your current racket like?',
      sub: 'Pick the closest type so we can find you a genuine step up.',
      showIf: (a) => a.level && a.level !== 'beginner' && !a.current_racket,
      options: [
        { v: 'round', label: 'A round / control racket', desc: 'Forgiving, control-first', icon: 'shield' },
        { v: 'teardrop', label: 'A teardrop / all-round racket', desc: 'Balanced power and control', icon: 'balance' },
        { v: 'diamond', label: 'A diamond / power racket', desc: 'Head-heavy, built to attack', icon: 'bolt' },
        { v: 'notsure', label: 'Not sure', desc: 'I just play with what I have', icon: 'compass' },
      ],
    },
    {
      id: 'dislike',
      title: 'What would you change about it?',
      sub: 'We’ll steer you toward a racket that fixes exactly that.',
      showIf: (a) => a.level && a.level !== 'beginner',
      options: [
        { v: 'power', label: 'I want more power', desc: 'More punch on smashes and drives', icon: 'bolt' },
        { v: 'control', label: 'I want more control', desc: 'More precision and placement', icon: 'shield' },
        { v: 'comfort', label: 'It’s harsh on my arm', desc: 'I want a softer, comfier feel', icon: 'feather' },
        { v: 'lighter', label: 'It feels heavy', desc: 'I want a lighter, faster swing', icon: 'arrows' },
        { v: 'spin', label: 'I want more spin', desc: 'A grippier face for bite', icon: 'compass' },
        { v: 'nothing', label: 'Nothing major', desc: 'Just exploring better options' },
      ],
    },
    {
      id: 'injury',
      title: 'Any elbow or wrist trouble?',
      sub: 'We’ll steer you toward arm-friendly rackets if so.',
      skippable: true,
      options: [
        { v: 'none', label: 'No, never', desc: 'Arm feels great' },
        { v: 'sometimes', label: 'Occasionally', desc: 'A twinge after long sessions' },
        { v: 'often', label: 'Yes, often', desc: 'Comfort is a priority' },
      ],
    },
    {
      id: 'age',
      title: 'Your age range?',
      sub: 'Helps us dial in weight and comfort.',
      options: [
        { v: '18-34', label: '18–34' },
        { v: '35-49', label: '35–49' },
        { v: '50+', label: '50+' },
      ],
    },
    {
      id: 'gender',
      title: 'Who’s this racket for?',
      sub: 'Some models are built with women-specific weight and balance.',
      options: [
        { v: 'men', label: 'Men’s' },
        { v: 'women', label: 'Women’s' },
      ],
    },
    {
      // "Women-suitable", not "women-only": a positive opt-in that prioritises lighter,
      // arm-friendly builds + women's-range designs. Every racket stays buyable either way.
      // (value 'feminine' kept for back-compat; it now means "prioritize women-suitable".)
      id: 'color',
      title: 'Want us to prioritize women-suitable rackets?',
      sub: 'Lighter, arm-friendly builds and women’s-range designs — never a hard filter.',
      showIf: (a) => a.gender === 'women',
      options: [
        { v: 'feminine', label: 'Yes, prioritize those', desc: 'Lighter, comfier, women’s-range first' },
        { v: 'any', label: 'No, just the best overall', desc: 'Rank by pure match' },
      ],
    },
    {
      id: 'budget',
      title: 'Got a budget you’d rather not go over?',
      sub: 'Just your ceiling — we match you to the right racket for your game, never the priciest.',
      options: [
        { v: '150', label: 'Keep it under $150' },
        { v: '250', label: 'Up to $250' },
        { v: '350', label: 'Up to $350' },
        { v: 'open', label: 'No limit — just match me right' },
      ],
    },
  ];

  /* ------------------------------------------------------------------ */
  /* Matching engine                                                     */
  /* ------------------------------------------------------------------ */

  const low = (v) => String(v == null ? '' : v).toLowerCase().trim();
  const arr = (v) => (Array.isArray(v) ? v.map(low).filter(Boolean) : []);
  const maxOf = (values, table) => {
    let best = null;
    for (const v of values) {
      const s = table[v];
      if (s != null && (best == null || s > best)) best = s;
    }
    return best;
  };
  // blend of best and average — a racket tagged with every shape is less *specifically*
  // a power (or control) racket than a pure single-shape one
  const blendOf = (values, table) => {
    const known = values.map((v) => table[v]).filter((s) => s != null);
    if (!known.length) return null;
    const max = Math.max(...known);
    const avg = known.reduce((a, b) => a + b, 0) / known.length;
    return max * 0.75 + avg * 0.25;
  };

  // Collapse colorways of the same model to one entry (e.g. "Optix V2 Lite White"
  // and "...Lilac" → one). Key = vendor + title with year, color codes and color
  // words stripped, so the top results are distinct rackets, not the same one twice.
  const COLOR_WORD =
    /\b(white|black|lilac|purple|violet|blue|navy|teal|aqua|yellow|gold|green|lime|red|crimson|orange|coral|pink|rose|grey|gray|silver|beige|sand|bronze)\b/g;
  const modelKey = (p) => {
    let t = String(p.title == null ? '' : p.title).toLowerCase();
    t = t
      .replace(/\b20\d{2}\b/g, ' ') // model years
      .replace(/\b[a-z]{2,3}\/[a-z]{2,3}\b/g, ' ') // colour codes e.g. BL/YL
      .replace(COLOR_WORD, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    return `${String(p.vendor == null ? '' : p.vendor).toLowerCase()}::${t}`;
  };

  // sub-attribute tables (keys are lowercased metafield strings)
  const WT = { '360g or less': 1, '360 - 370g': 0.55, '370g or more': 0.1 };
  const STYLE_PROFILES = {
    defensive: {
      shape: { round: 1, teardrop: 0.6, hybrid: 0.55, 'inverted teardrop': 0.6, diamond: 0.15 },
      play: { control: 1, defensive: 1, 'all around': 0.55, versatile: 0.55, power: 0.15, aggresive: 0.1 },
      bal: { low: 1, medium: 0.6, high: 0.1 },
      core: { soft: 0.85, medium: 0.6, hard: 0.3 },
      reason: 'Control-first build for defensive play',
    },
    allround: {
      shape: { teardrop: 1, hybrid: 1, 'inverted teardrop': 0.9, round: 0.6, diamond: 0.35 },
      play: { versatile: 1, 'all around': 1, control: 0.6, defensive: 0.5, power: 0.5, aggresive: 0.45 },
      bal: { medium: 1, low: 0.6, high: 0.5 },
      core: { medium: 1, soft: 0.7, hard: 0.6 },
      reason: 'Balanced shape for all-court play',
    },
    aggressive: {
      shape: { diamond: 1, hybrid: 0.6, teardrop: 0.55, 'inverted teardrop': 0.5, round: 0.2 },
      play: { power: 1, aggresive: 1, versatile: 0.5, 'all around': 0.5, control: 0.15, defensive: 0.1 },
      bal: { high: 1, medium: 0.6, low: 0.15 },
      core: { hard: 0.9, medium: 0.7, soft: 0.35 },
      reason: 'Power profile built to finish points',
    },
  };
  // Experienced-player branch: each stated gripe with their current racket inverts to the
  // attributes that fix it (e.g. "no power" → diamond/high-balance/hard). Weights inside a
  // profile are relative — the engine normalises by the ones with data, so a missing field
  // never tanks the criterion.
  const DISLIKE_PROFILES = {
    // power: NOT a static table — computed by powerProfile() below, because "want more power" is
    // shape-relative (step up from the player's current shape) and level-aware (true power vs output).
    control: {
      shape: { round: 1, teardrop: 0.7, hybrid: 0.65, 'inverted teardrop': 0.6, diamond: 0.15 },
      bal: { low: 1, medium: 0.6, high: 0.15 },
      core: { soft: 0.9, medium: 0.7, hard: 0.4 },
      reason: 'More control and precision',
    },
    comfort: { armScore: true, reason: 'Gentler, arm-friendly feel' },
    lighter: {
      wt: { '360g or less': 1, '360 - 370g': 0.5, '370g or more': 0.1 },
      bal: { low: 1, medium: 0.6, high: 0.2 },
      reason: 'Lighter, faster to swing',
    },
    spin: {
      surf: { rough: 1, '3d': 0.85, smooth: 0.2 },
      shape: { teardrop: 0.8, diamond: 0.8, hybrid: 0.7, 'inverted teardrop': 0.65, round: 0.4 },
      reason: 'Grippier face for more spin',
    },
  };
  // Reduce a (possibly multi-value) shape metafield to one bucket for the "step up from here" logic.
  // Round-only ⇒ round; diamond-only ⇒ diamond; anything mixed/hybrid/teardrop ⇒ teardrop (the middle).
  function primaryShape(cs) {
    if (!cs || !cs.length) return null;
    if (cs.includes('diamond') && !cs.includes('round') && !cs.includes('teardrop')) return 'diamond';
    if (cs.includes('round') && !cs.includes('diamond') && !cs.includes('teardrop') && !cs.includes('hybrid')) return 'round';
    return 'teardrop';
  }

  // "I want MORE power than my current racket." Two things the naive "diamond + hard + head-heavy"
  // inversion got wrong (KB §7, power vs output):
  //   1. SHAPE is relative to what they already play, and steps up by ~one notch — never round→diamond.
  //      round→teardrop · teardrop→(more powerful) teardrop/hybrid · diamond→diamond. Switching a round
  //      player straight to a diamond is too big a jump; a teardrop player keeps the shape they know.
  //   2. POWER is level-aware: "true power" (rigid, head-heavy, small sweet spot — Coello/Lebrón/Galán
  //      pro frames) only pays off for an ADVANCED technique. An IMPROVING player gets more usable power
  //      from "easy power"/OUTPUT — a softer, spring-loaded face + larger sweet spot at a manageable
  //      balance; a hard pro racket just won't fire for them.
  function powerProfile(level, curShape) {
    const advanced = level === 'advanced';
    let shape;
    if (curShape === 'round') {
      shape = { teardrop: 1, hybrid: 0.9, 'inverted teardrop': 0.85, round: 0.25, diamond: 0.2 };
    } else if (curShape === 'diamond') {
      shape = { diamond: 1, hybrid: 0.85, 'inverted teardrop': 0.7, teardrop: 0.5, round: 0.1 };
    } else {
      // teardrop (keep the familiar shape, just more powerful) or unknown → offensive-forward,
      // but do NOT lead with diamond
      shape = { teardrop: 1, hybrid: 0.9, 'inverted teardrop': 0.85, diamond: 0.6, round: 0.15 };
    }
    const bal = advanced ? { high: 1, medium: 0.6, low: 0.2 } : { medium: 1, high: 0.75, low: 0.45 };
    const core = advanced ? { hard: 1, medium: 0.75, soft: 0.45 } : { soft: 1, medium: 0.95, hard: 0.5 };
    return { shape, bal, core, reason: 'More power than your current racket' };
  }

  const LEVEL_TIER = { beginner: 0, intermediate: 1, advanced: 2, pro: 3 };
  const LEVEL_REASON = {
    beginner: 'Beginner-friendly by design',
    intermediate: 'Built for improving players',
    advanced: 'Trusted at competitive level',
  };
  const FEEL_MAP = { soft: 'soft', medium: 'medium', firm: 'hard' };
  const FEEL_REASON = {
    soft: 'Soft core — plush comfort',
    medium: 'Medium core — balanced touch',
    firm: 'Firm core — direct feedback',
  };

  function armScore(p) {
    const subs = [];
    const hand = arr(p.hand);
    if (hand.length) subs.push(maxOf(hand, { forgiving: 1, 'easy to play': 1, demanding: 0 }) ?? 0.5);
    const core = arr(p.core);
    if (core.length) subs.push(maxOf(core, { soft: 1, medium: 0.6, hard: 0.15 }) ?? 0.5);
    const bal = arr(p.bal);
    if (bal.length) subs.push(maxOf(bal, { low: 1, medium: 0.6, high: 0.15 }) ?? 0.5);
    const wt = arr(p.wt);
    if (wt.length) subs.push(maxOf(wt, WT) ?? 0.5);
    if (!subs.length) return null;
    return subs.reduce((a, b) => a + b, 0) / subs.length;
  }

  // The "arm-wrecker" archetype — heavy and/or head-heavy builds that are hard on arms and
  // tendons. Used to keep these away from non-competitive women (beginner: excluded;
  // intermediate: down-ranked). Deliberately NOT triggered by a light head-heavy racket
  // (e.g. Head Extreme Motion, ≤360g) — those are fine and stay recommendable.
  function armDemanding(p) {
    const bal = arr(p.bal);
    const wt = arr(p.wt);
    const core = arr(p.core);
    const hand = arr(p.hand);
    const headHeavy = bal.includes('high');
    const heavy = wt.includes('370g or more');
    const firm = core.includes('hard');
    const demandingHand = hand.includes('demanding');
    return (headHeavy && (heavy || firm || demandingHand)) || (heavy && (firm || demandingHand));
  }

  // "Effective heft" for women — what a racket FEELS like, not its scale number. Felt weight = the
  // weight band shifted by balance/shape: a diamond / head-heavy frame carries its mass in the head and
  // feels much heavier (more forearm load), while a round / low-balance frame feels lighter (mass toward
  // the shaft) — so a 360g round can feel lighter than a 355g diamond. Catalogue weight is also BARE (no
  // grip/overgrip — most players add 5–8g+), so 370g+ plays genuinely heavy. We map heft → a suitability
  // score: beginner/intermediate women should stay ~350–360g; advanced/strong women tolerate more (up to
  // the 360–370g band, especially teardrop/round). Returns null when weight/balance/shape are all unknown
  // (don't penalise missing data). Men are not weight-capped here. See KB §6.
  function womenWeightScore(p, advanced) {
    const wt = arr(p.wt);
    const bal = arr(p.bal);
    const shape = arr(p.shape);
    if (!wt.length && !bal.length && !shape.length) return null;
    // base: heavier band = higher; unknown weight is treated as neutral (mid) so balance still speaks.
    const base = wt.includes('370g or more') ? 2 : wt.includes('360 - 370g') ? 1 : wt.length ? 0 : 1;
    // balance modifier (preferred signal); fall back to shape only when balance is absent.
    let mod = 0;
    if (bal.length) {
      mod = Math.max(...bal.map((v) => (v === 'high' ? 0.7 : v === 'low' ? -0.7 : 0)));
    } else if (shape.length) {
      const hasD = shape.includes('diamond');
      const hasR = shape.includes('round');
      mod = hasD && !hasR ? 0.5 : hasR && !hasD ? -0.5 : 0;
    }
    const heft = base + mod; // ≈ -0.7 (light round) … 2.7 (heavy diamond)
    const s = advanced
      ? heft <= 0.7 ? 1 : heft <= 1.3 ? 0.95 : heft <= 1.7 ? 0.85 : heft <= 2.0 ? 0.65 : 0.45
      : heft <= 0.0 ? 1 : heft <= 0.7 ? 0.9 : heft <= 1.3 ? 0.6 : heft <= 2.0 ? 0.3 : 0.12;
    return { s, heft };
  }

  function buildCriteria(a, ctx) {
    const c = [];

    // 1) Padel level
    c.push({
      key: 'level',
      weight: 26,
      judge(p) {
        const L = arr(p.lvl);
        if (!L.length) return null;
        const targets = a.level === 'advanced' ? ['advanced', 'pro'] : [a.level];
        if (L.some((v) => targets.includes(v))) return { s: 1, reason: LEVEL_REASON[a.level] };
        const at = LEVEL_TIER[a.level === 'advanced' ? 'advanced' : a.level];
        let dist = 3;
        for (const v of L) if (LEVEL_TIER[v] != null) dist = Math.min(dist, Math.abs(LEVEL_TIER[v] - at));
        return { s: dist <= 1 ? 0.45 : dist === 2 ? 0.15 : 0.05 };
      },
    });

    // 2) Play style (the highest signal)
    const styleKey = a.style === 'notsure' || !a.style ? 'allround' : a.style;
    const prof = STYLE_PROFILES[styleKey];
    c.push({
      key: 'style',
      weight: a.style === 'notsure' ? 12 : 30,
      judge(p) {
        const subs = [];
        const shape = arr(p.shape);
        if (shape.length) subs.push([blendOf(shape, prof.shape) ?? 0.4, 0.35]);
        const play = arr(p.play);
        if (play.length) subs.push([blendOf(play, prof.play) ?? 0.4, 0.35]);
        const bal = arr(p.bal);
        if (bal.length) subs.push([maxOf(bal, prof.bal) ?? 0.4, 0.2]);
        const core = arr(p.core);
        if (core.length) subs.push([maxOf(core, prof.core) ?? 0.4, 0.1]);
        if (!subs.length) return null;
        const wsum = subs.reduce((t, [, w]) => t + w, 0);
        const s = subs.reduce((t, [v, w]) => t + v * w, 0) / wsum;
        return { s, reason: s >= 0.75 && a.style !== 'notsure' ? prof.reason : undefined };
      },
    });

    // 3) Feel — BUT a "firm" preference is ignored for an injured arm: firmness and comfort are not
    //    compatible, and you should never steer a bad elbow toward a stiff racket. The comfort criteria
    //    (injury / age / dislike=comfort) then pull toward the SOFTEST racket that still fits their
    //    style (e.g. a soft but aggressive frame). Soft/medium feel answers are kept as normal. KB §8.
    const armInjured = a.injury && a.injury !== 'none';
    if (a.feel && a.feel !== 'notsure' && !(armInjured && a.feel === 'firm')) {
      const want = FEEL_MAP[a.feel];
      c.push({
        key: 'feel',
        weight: 18,
        judge(p) {
          const cores = arr(p.core);
          if (!cores.length) return null;
          if (cores.includes(want)) return { s: 1, reason: FEEL_REASON[a.feel] };
          const order = ['soft', 'medium', 'hard'];
          let dist = 2;
          for (const v of cores) if (order.includes(v)) dist = Math.min(dist, Math.abs(order.indexOf(v) - order.indexOf(want)));
          return { s: dist === 1 ? 0.45 : 0.05 };
        },
      });
    }

    // 4) Arm comfort (elbow/wrist)
    const sev = a.injury === 'often' ? 1 : a.injury === 'sometimes' ? 0.5 : 0;
    if (sev > 0) {
      c.push({
        key: 'injury',
        weight: 26 * sev,
        judge(p) {
          const s = armScore(p);
          if (s == null) return null;
          return { s, reason: s >= 0.7 ? 'Arm-friendly build' : undefined };
        },
      });
    }

    // 5) Age comfort
    if (a.age === '50+' || a.age === '35-49') {
      c.push({
        key: 'age',
        weight: a.age === '50+' ? 10 : 5,
        judge(p) {
          const s = armScore(p);
          if (s == null) return null;
          return { s, reason: a.age === '50+' && s >= 0.75 ? 'Light swing, easy on the body' : undefined };
        },
      });
    }

    // 6) Prior racket-sport background (beginners). Tennis converts want some head presence + a
    //    textured face for the "string feel", but as NEW padel players they need a forgiving shape,
    //    not a raw diamond (teardrop/hybrid compromise; full diamond only if they also play
    //    aggressive). The paddle and table-tennis sports transfer far less — shape barely matters,
    //    so we just nudge toward a familiar, forgiving, maneuverable feel at a low weight.
    if (a.level === 'beginner' && a.prior_sport && a.prior_sport !== 'none') {
      const sport = a.prior_sport;
      if (sport === 'tennis') {
        const diamondScore = a.style === 'aggressive' ? 1 : 0.55;
        c.push({
          key: 'prior_sport',
          weight: 16,
          judge(p) {
            const subs = [];
            const shape = arr(p.shape);
            if (shape.length) subs.push([blendOf(shape, { teardrop: 1, hybrid: 0.95, 'inverted teardrop': 0.85, diamond: diamondScore, round: 0.1 }) ?? 0.4, 0.4]);
            const bal = arr(p.bal);
            if (bal.length) subs.push([maxOf(bal, { high: 0.9, medium: 0.85, low: 0.4 }) ?? 0.5, 0.25]);
            const surf = arr(p.surf);
            if (surf.length) subs.push([maxOf(surf, { rough: 1, '3d': 0.8, smooth: 0.2 }) ?? 0.5, 0.2]);
            const core = arr(p.core);
            if (core.length) subs.push([maxOf(core, { hard: 1, medium: 0.7, soft: 0.4 }) ?? 0.5, 0.15]);
            if (!subs.length) return null;
            const wsum = subs.reduce((t, [, w]) => t + w, 0);
            const s = subs.reduce((t, [v, w]) => t + v * w, 0) / wsum;
            return { s, reason: s >= 0.7 ? 'Suits your tennis background' : undefined };
          },
        });
      } else if (sport === 'pball') {
        // paddle-court sport: shape barely matters; nudge toward a familiar even/round-to-teardrop,
        // forgiving feel (NOT head-heavy power), at a deliberately low weight.
        c.push({
          key: 'prior_sport',
          weight: 8,
          judge(p) {
            const subs = [];
            const shape = arr(p.shape);
            if (shape.length) subs.push([blendOf(shape, { round: 1, teardrop: 1, hybrid: 0.85, 'inverted teardrop': 0.8, diamond: 0.45 }) ?? 0.5, 0.45]);
            const bal = arr(p.bal);
            if (bal.length) subs.push([maxOf(bal, { low: 1, medium: 0.9, high: 0.5 }) ?? 0.6, 0.3]);
            const hand = arr(p.hand);
            if (hand.length) subs.push([maxOf(hand, { forgiving: 1, 'easy to play': 1, demanding: 0.5 }) ?? 0.7, 0.25]);
            if (!subs.length) return null;
            const wsum = subs.reduce((t, [, w]) => t + w, 0);
            const s = subs.reduce((t, [v, w]) => t + v * w, 0) / wsum;
            return { s, reason: s >= 0.75 ? 'Familiar, forgiving feel' : undefined };
          },
        });
      } else {
        // ping pong / badminton / other: fast wrists, value a light, maneuverable, forgiving racket.
        c.push({
          key: 'prior_sport',
          weight: 6,
          judge(p) {
            const s = armScore(p); // light + forgiving + head-light scores high
            if (s == null) return null;
            return { s, reason: s >= 0.75 ? 'Light, maneuverable feel' : undefined };
          },
        });
      }
    }

    // 6b) Current-racket gripe (experienced players) — invert the dislike into the attributes
    //     that fix it. High weight: it's an explicit, specific ask.
    if (a.level && a.level !== 'beginner' && a.dislike && a.dislike !== 'nothing') {
      // "Want more power" is shape-relative + level-aware (see powerProfile / KB §7); every other gripe
      // uses the static inversion table. Current shape comes from the picker, or the exact racket (Phase C).
      let curShape = a.current_shape && a.current_shape !== 'notsure' ? a.current_shape : null;
      if (!curShape && ctx && ctx.currentP) curShape = primaryShape(arr(ctx.currentP.shape));
      const prof = a.dislike === 'power' ? powerProfile(a.level, curShape) : DISLIKE_PROFILES[a.dislike];
      if (prof) {
        c.push({
          key: 'dislike',
          weight: 22,
          judge(p) {
            if (prof.armScore) {
              const s = armScore(p);
              if (s == null) return null;
              return { s, reason: s >= 0.7 ? prof.reason : undefined };
            }
            const subs = [];
            if (prof.shape) {
              const v = arr(p.shape);
              if (v.length) subs.push([blendOf(v, prof.shape) ?? 0.4, 0.4]);
            }
            if (prof.bal) {
              const v = arr(p.bal);
              if (v.length) subs.push([maxOf(v, prof.bal) ?? 0.4, 0.3]);
            }
            if (prof.core) {
              const v = arr(p.core);
              if (v.length) subs.push([maxOf(v, prof.core) ?? 0.4, 0.2]);
            }
            if (prof.wt) {
              const v = arr(p.wt);
              if (v.length) subs.push([maxOf(v, prof.wt) ?? 0.4, 0.45]);
            }
            if (prof.surf) {
              const v = arr(p.surf);
              if (v.length) subs.push([maxOf(v, prof.surf) ?? 0.4, 0.5]);
            }
            if (!subs.length) return null;
            const wsum = subs.reduce((t, [, w]) => t + w, 0);
            const s = subs.reduce((t, [v, w]) => t + v * w, 0) / wsum;
            return { s, reason: s >= 0.7 ? prof.reason : undefined };
          },
        });
      }
    }

    // 6c) Freshness — when an experienced player wants a change, a racket that's only the same
    //     shape they already own is a lateral move, not an upgrade. Low weight: a gentle nudge.
    if (!a.current_racket && a.level && a.level !== 'beginner' && a.current_shape && a.current_shape !== 'notsure' && a.dislike && a.dislike !== 'nothing' && a.dislike !== 'power') {
      c.push({
        key: 'freshness',
        weight: 8,
        judge(p) {
          const shape = arr(p.shape);
          if (!shape.length) return null;
          const sameOnly = shape.length === 1 && shape[0] === a.current_shape;
          const sameAny = shape.includes(a.current_shape);
          return { s: sameOnly ? 0.3 : sameAny ? 0.7 : 1 };
        },
      });
    }

    // 6d) Avoid-similar (Phase C negative algorithm) — when the player picked their EXACT current
    //     racket from our catalogue, never re-recommend it, and down-rank near-clones (same brand +
    //     shape + balance), so we point them at a genuine change. Skipped entirely when they didn't
    //     pick one (racket not carried here → handled by the freshness nudge above instead).
    if (a.current_racket && ctx && ctx.currentP) {
      const cur = ctx.currentP;
      const curBrand = low(cur.vendor);
      const curShapes = arr(cur.shape);
      const curBal = arr(cur.bal);
      c.push({
        key: 'avoid_similar',
        weight: 12,
        judge(p) {
          if (p.handle === cur.handle) return { s: 0 }; // the racket they already own — never again
          const sameBrand = !!curBrand && low(p.vendor) === curBrand;
          const sameShape = curShapes.length > 0 && arr(p.shape).some((s) => curShapes.includes(s));
          const sameBal = curBal.length > 0 && arr(p.bal).some((b) => curBal.includes(b));
          if (sameBrand && sameShape && sameBal) return { s: 0.3 };
          if (sameBrand && sameShape) return { s: 0.55 };
          if (sameShape && sameBal) return { s: 0.7 };
          return { s: 1 };
        },
      });
    }

    // 7) Women-suitable preference — a POSITIVE, non-restrictive re-sort (men never see it).
    //    `for_whom` now means "women's range" (a curated design/suitability flag, NOT a gender
    //    lock), so a tagged racket scores top; otherwise we reward arm-friendly builds, because
    //    "women-suitable" is about comfort + fit, not just colourway.
    if (a.gender === 'women' && a.color === 'feminine') {
      c.push({
        key: 'color',
        weight: 12,
        judge(p) {
          const who = arr(p.who);
          if (who.includes('women')) return { s: 1, reason: 'Women’s-range pick' };
          // not a women's-range design → score by suitability (lighter/forgiving = comfier)
          return { s: armDemanding(p) ? 0.2 : 0.7 };
        },
      });
    }

    // 8) Women weight & balance ("effective heft"). ALWAYS on for women (a comfort/fit rule, not a
    //    design opt-in like #7): the felt weight — band shifted by balance/shape — should stay
    //    manageable. Beginner/intermediate women steer to ~350–360g & lighter-feeling balances; advanced
    //    women get a higher tolerance (360–370g OK, esp. teardrop/round). Men are never weight-capped.
    if (a.gender === 'women') {
      const advanced = a.level === 'advanced';
      c.push({
        key: 'womenweight',
        weight: 16,
        judge(p) {
          const r = womenWeightScore(p, advanced);
          if (r == null) return null;
          return { s: r.s, reason: r.s >= 0.85 ? 'Manageable weight & balance' : undefined };
        },
      });
    }

    return c;
  }

  function hardFilter(products, a, opts) {
    // Osaka is dropshipped (poor margins) — hard-excluded from the quiz at the engine level so it's
    // guaranteed out regardless of the `excluded_brands` setting. Any setting brands merge on top.
    const excluded = ['osaka', ...((opts && opts.excludedBrands) || []).map((b) => low(b))];
    const cap = a.budget === 'open' ? Infinity : Number(a.budget || Infinity);
    // Excluded brands (e.g. dropshipped lines we make less margin on) never get recommended.
    const brandOk = (p) => {
      const v = low(p.vendor);
      return !excluded.some((b) => b && v.includes(b));
    };
    const genderOk = (p) => {
      const who = arr(p.who);
      if (!who.length) return true; // untagged ⇒ unisex
      if (a.gender === 'men') return who.includes('men');
      if (a.gender === 'women') return who.includes('women');
      return true;
    };
    // Heavy + head-heavy rackets are hard on arms and tendons. For a beginner woman they're a
    // poor first racket, so we exclude them outright (competitive women are exempt — see the
    // down-rank in rank() for intermediates). This is a SAFETY rule, never relaxed by budget.
    const excludeDemanding = a.gender === 'women' && a.level === 'beginner';
    const armSafe = (p) => !(excludeDemanding && armDemanding(p));
    // Tennis-elbow safety: VERY stiff rackets (Hard core — e.g. Adidas Metalbone HRD, Babolat Juan
    // Lebrón 3.0) are brutal on a bad elbow even though they're great power rackets for healthy arms.
    // With a FREQUENT injury we exclude them outright; "sometimes" only down-ranks (see rank()).
    // No injury ⇒ nothing suppressed — power players keep the firm/hard options.
    const veryStiff = (p) => arr(p.core).includes('hard');
    const injurySafe = (p) => !(a.injury && a.injury !== 'none' && veryStiff(p));
    // Entry rackets (level = Beginner ONLY, e.g. Head Vibe / Head EVO Extreme) are easy but limiting —
    // an experienced player outgrows them fast. Beginners can't go wrong with them so they stay there;
    // for intermediate/advanced we drop them so we recommend a racket they can grow with.
    const isEntry = (p) => { const L = arr(p.lvl); return L.length > 0 && L.every((v) => v === 'beginner'); };
    const levelOk = (p) => !(a.level && a.level !== 'beginner' && isEntry(p));
    const keep = (p) => brandOk(p) && genderOk(p) && armSafe(p) && injurySafe(p) && levelOk(p);
    let pool = products.filter((p) => keep(p) && Number(p.price) <= cap);
    let stretched = false;
    if (pool.length < 3 && cap !== Infinity) {
      // not enough inside budget — relax the cap, flag over-budget picks honestly
      stretched = true;
      pool = products.filter(keep);
    }
    return { pool, cap, stretched };
  }

  // Soft brand-diversity: among the top `window` picks, prefer DISTINCT brands so we
  // never show e.g. 3 of one brand (reads as biased and erodes trust). "Soft" = we only
  // swap in a different-brand racket when its match score is within `tolerance` of the
  // best remaining one — never surface a notably worse-fitting racket just for variety.
  // On by default; pass opts.brandDiversity === false to disable, or tune
  // opts.diversityWindow / opts.diversityTolerance.
  function diversifyByBrand(list, opts) {
    opts = opts || {};
    if (opts.brandDiversity === false || list.length < 3) return list;
    const lc = (s) => String(s == null ? '' : s).toLowerCase();
    const window = opts.diversityWindow || 3;
    const tol = opts.diversityTolerance != null ? opts.diversityTolerance : 0.06;
    const remaining = list.slice();
    const out = [];
    const used = new Set();
    while (remaining.length) {
      let idx = 0;
      if (out.length < window) {
        const best = remaining[0];
        const diffIdx = remaining.findIndex((r) => !used.has(lc(r.p.vendor)));
        if (diffIdx > 0 && best.score - remaining[diffIdx].score <= tol) idx = diffIdx;
      }
      const picked = remaining.splice(idx, 1)[0];
      out.push(picked);
      used.add(lc(picked.p.vendor));
    }
    return out;
  }

  function rank(products, a, opts) {
    opts = opts || {};
    const priority = opts.priorityBrands || [];
    const boost = opts.brandBoost || 0;
    // Priority brands (ones we want to push) get a small sort nudge — enough to win among
    // comparable matches, not enough to surface a genuinely worse racket. Honest match % is
    // unchanged (boost touches rankScore, not score).
    const brandBonus = (vendor) => (boost && priority.some((b) => b && low(vendor).includes(b)) ? boost : 0);
    // Intermediate women still SEE heavy/head-heavy rackets (they're not excluded like beginners)
    // but they're pushed down — a comparable arm-friendlier racket should win. Advanced/competitive
    // women get no penalty.
    const demandPenalty = (p) =>
      a.gender === 'women' && a.level === 'intermediate' && armDemanding(p) ? 0.25 : 0;
    // Phase C: resolve the player's exact current racket (if they picked one) so the
    // avoid-similar criterion can down-rank near-clones. Null when not carried/skipped.
    const currentP = a.current_racket ? products.find((p) => p.handle === a.current_racket) || null : null;
    const criteria = buildCriteria(a, { currentP });
    const { pool, cap, stretched } = hardFilter(products, a, opts);
    const scored = pool.map((p) => {
      let wsum = 0;
      let total = 0;
      let missing = 0;
      const reasons = [];
      for (const cr of criteria) {
        const r = cr.judge(p);
        if (r == null) {
          missing += 1;
          continue;
        }
        wsum += cr.weight;
        total += r.s * cr.weight;
        if (r.reason) reasons.push({ reason: r.reason, value: r.s * cr.weight });
      }
      const score = wsum > 0 ? total / wsum : 0.3;
      const rankScore = score - missing * 0.03 + brandBonus(p.vendor) - demandPenalty(p); // sparse sinks; priority brands nudge up; demanding rackets sink for intermediate women
      // Displayed match % — DISPLAY ONLY (never touches rankScore/order). Raw scores cluster high for
      // clean fits (a generic beginner racket genuinely matches every active criterion → ~0.99), so a
      // straight score×100 made everything read 95–99 and a $110 racket showed "99% match" — which reads
      // fake. Rescale the realistic score band (~0.55–1.0) onto 40–95 so the % spreads and feels like a
      // real fit signal: nothing ever shows above 95, a strong match ~90, a decent match ~80, a stretch
      // ~70. Order is unchanged. See KB §9.
      const pct = Math.max(40, Math.min(95, Math.round(58 + (score - 0.55) * 82)));
      reasons.sort((x, y) => y.value - x.value);
      const chips = reasons.slice(0, 3).map((r) => r.reason);
      if (stretched && Number(p.price) > cap) chips.unshift('Worth the stretch');
      return { p, score, rankScore, pct, chips };
    });
    scored.sort(
      (x, y) =>
        y.rankScore - x.rankScore || Number(y.p.bs) - Number(x.p.bs) || Number(x.p.price) - Number(y.p.price)
    );
    // collapse colourways of the same model — keep the best-scoring one (list is sorted)
    const seenModels = new Set();
    const deduped = [];
    for (const r of scored) {
      const key = modelKey(r.p);
      if (seenModels.has(key)) continue;
      seenModels.add(key);
      deduped.push(r);
    }
    // Soft brand-diversity pass so the top picks aren't all one brand (the "3 Head" problem).
    const ordered = diversifyByBrand(deduped, opts);
    // rank-aware display: ties broken by best-seller/price are real ranking signal,
    // so displayed % descends strictly — never a wall of identical 99s
    let prev = Infinity;
    for (const r of ordered) {
      if (r.pct >= prev) r.pct = Math.max(40, prev - 1);
      prev = r.pct;
    }
    return ordered;
  }

  /* ------------------------------------------------------------------ */
  /* UI                                                                  */
  /* ------------------------------------------------------------------ */

  // Racket Finder v2 screens (assets/racket-finder.js) read the engine through this handle and
  // never change it. Kept below the UI banner so racket-brain's engine extraction is unaffected.
  window.PadelFinderEngine = { QUESTIONS, rank, buildCriteria, hardFilter, modelKey, version: PF_ENGINE_VERSION };

  const fmt = (n) => {
    const v = Number(n);
    return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  // single render sink: clears the node, then parses our static templates + esc()'d values
  const render = (node, html) => {
    node.replaceChildren();
    node.insertAdjacentHTML('beforeend', html);
  };
  // Every quiz event goes to dataLayer (GTM, unchanged) and to Shopify.analytics.publish, which the
  // "PadelUSA OS analytics" custom pixel forwards to os.padelusa.com/analytics (Racket Finder
  // funnel). The published copy always carries pf_session — the same random per-visit id the quiz
  // writes onto the racket's _pf_session line property — so the funnel joins orders exactly. No
  // personal data: answers are option ids or catalog handles, never the email.
  const track = (event, detail, session) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, detail || {}));
    } catch (e) {
      /* analytics must never break the quiz */
    }
    try {
      window.Shopify?.analytics?.publish?.(event, Object.assign({}, detail || {}, session ? { pf_session: session } : {}));
    } catch (e) {
      /* analytics must never break the quiz */
    }
  };

  class ProductFinderApp extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pfInit) return;
      this.dataset.pfInit = '1';
      try {
        const data = JSON.parse(this.querySelector('[data-pf-products]')?.textContent || '{}');
        this.products = Array.isArray(data.products) ? data.products : [];
      } catch (e) {
        this.products = [];
      }
      this.answers = {};
      this.history = [];
      this.visibleResults = 0;
      this.ranked = [];
      this.offerDone = false;
      const brandList = (v) =>
        (v || '')
          .toLowerCase()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      this.brandOpts = {
        priorityBrands: brandList(this.dataset.pfPriorityBrands),
        excludedBrands: brandList(this.dataset.pfExcludedBrands),
        brandBoost: (Number(this.dataset.pfBrandBoost) || 0) / 100,
      };
      // Attribution: a per-visit id (joins quiz analytics events ↔ the order's _pf_session line
      // property) + the order-source label written onto each quiz add.
      this.attributionLabel = (this.dataset.pfAttribution || '').trim();
      this.sessionId = `pf_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      // Stream B: the storefront AI agent's public /events URL. Empty ⇒ the beacon is a no-op.
      this.agentEventsUrl = (this.dataset.pfAgentEvents || '').trim();
      this.panels = {
        intro: this.querySelector('[data-pf-panel="intro"]'),
        quiz: this.querySelector('[data-pf-panel="quiz"]'),
        calc: this.querySelector('[data-pf-panel="calc"]'),
        results: this.querySelector('[data-pf-panel="results"]'),
      };
      this.mount = this.querySelector('[data-pf-question-mount]');
      this.progress = this.querySelector('[data-pf-progress]');
      this.progressBar = this.querySelector('[data-pf-progress-bar]');
      this.progressCount = this.querySelector('[data-pf-progress-count]');
      const countEl = this.querySelector('[data-pf-count]');
      if (countEl) countEl.textContent = String(this.products.length);

      this.querySelector('[data-pf-start]')?.addEventListener('click', () => this.start());
      this.querySelector('[data-pf-back]')?.addEventListener('click', () => this.back());
      this.querySelector('[data-pf-more]')?.addEventListener('click', () => this.renderResults(true));
      this.querySelector('[data-pf-retake]')?.addEventListener('click', () => this.retake());
      this.show('intro');
      this.restore();
    }

    track(event, detail) {
      track(event, detail, this.sessionId);
    }

    visibleQuestions() {
      return QUESTIONS.filter((q) => !q.showIf || q.showIf(this.answers));
    }

    show(name) {
      this.dataset.pfPanel = name;
      for (const [key, el] of Object.entries(this.panels)) {
        if (!el) continue;
        el.hidden = key !== name;
      }
      if (this.progress) this.progress.hidden = name !== 'quiz';
    }

    // Gentle, header-aware scroll. Only used where it genuinely helps (entering
    // results, retaking) — never on Start, which previously jumped the page up and
    // tucked the sticky header out of view. scroll-margin-top (CSS) keeps the menu visible.
    scrollToSelf() {
      const overlay = this.closest('product-finder-dialog');
      if (overlay) {
        overlay.body.scrollTo({ top: 0, behavior: 'instant' });
        return;
      }
      try {
        this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch (e) {
        this.scrollIntoView();
      }
    }

    // Stream B (AI-agent integration): mirror the key quiz lifecycle events to the agent's public
    // /events endpoint so quiz sessions accumulate next to chat events in Supabase (the substrate for
    // the conversion-feedback loop). Fire-and-forget via sendBeacon (survives the add-to-cart
    // navigation); no-op until `data-pf-agent-events` is set. pf_session is added automatically so a
    // player's events join up. The email is NEVER sent here — it goes to Klaviyo. Analytics must never
    // break the quiz. Allow-listed event types + payloads: docs/product-finder-agent-integration.md §3.
    // The body goes as text/plain (CORS-safelisted) so the beacon is a simple request: a JSON Blob
    // makes sendBeacon send a credentialed preflight, which the agent's /events CORS rejects — the
    // events were silently dropped until 2026-10-09. The endpoint parses the text as JSON.
    beacon(eventType, data) {
      if (!this.agentEventsUrl) return;
      try {
        const body = JSON.stringify({
          event_type: eventType,
          conversation_id: this.sessionId, // groups a player's quiz events; a chat would use its own id
          data: Object.assign({ pf_session: this.sessionId }, data || {}),
        });
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon && navigator.sendBeacon(this.agentEventsUrl, blob)) return;
        fetch(this.agentEventsUrl, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          mode: 'no-cors',
          credentials: 'omit',
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        /* analytics must never break the quiz */
      }
    }

    start() {
      this.answers = {};
      this.history = [];
      this.track('pf_start');
      this.beacon('quiz_started');
      this.show('quiz');
      this.renderQuestion(this.visibleQuestions()[0]);
      this.persist(false);
    }

    focusStep(selector = '.pf-q__title') {
      // Timers may finish while a dismissed overlay stays cached. Never steal page focus.
      const dialog = this.closest('dialog');
      if (dialog && !dialog.open) return;
      const target = this.querySelector(selector);
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
      if (dialog) this.scrollToSelf();
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem('pf-state-v1') || 'null');
        if (saved && saved.answers && typeof saved.answers === 'object' && !Array.isArray(saved.answers)) {
          this.answers = saved.answers;
          if (saved.sessionId) this.sessionId = saved.sessionId;
          if (saved.done) this.finish(true);
          else if (saved.current) {
            const flow = this.visibleQuestions();
            const question = flow.find((q) => q.id === saved.current);
            if (!question) return;
            this.history = Array.isArray(saved.history) ? saved.history.filter((id) => flow.some((q) => q.id === id)) : [];
            this.show('quiz');
            this.renderQuestion(question);
          }
        }
      } catch (e) {
        /* fresh start */
      }
    }

    persist(done) {
      try {
        sessionStorage.setItem('pf-state-v1', JSON.stringify({ answers: this.answers, done: !!done, current: this.current?.id, history: this.history, sessionId: this.sessionId }));
      } catch (e) {
        /* private mode */
      }
    }

    renderQuestion(q) {
      if (!q) return this.finish();
      this.current = q;
      const flow = this.visibleQuestions();
      const idx = flow.indexOf(q);
      if (this.progressBar) this.progressBar.style.width = `${Math.round((idx / flow.length) * 100)}%`;
      if (this.progressCount) this.progressCount.textContent = `${idx + 1} / ${flow.length}`;

      if (q.type === 'search') return this.renderSearchQuestion(q);

      // If ANY option has an icon, reserve the icon column for the ones without one so labels align.
      const hasIcons = q.options.some((o) => o.icon && ICONS[o.icon]);
      const opts = q.options
        .map((o, i) => {
          const icon =
            o.icon && ICONS[o.icon]
              ? `<span class="pf-opt__icon">${ICONS[o.icon]}</span>`
              : hasIcons
                ? '<span class="pf-opt__icon pf-opt__icon--empty" aria-hidden="true"></span>'
                : '';
          const desc = o.desc ? `<span class="pf-opt__desc">${esc(o.desc)}</span>` : '';
          const selected = this.answers[q.id] === o.v ? ' is-selected' : '';
          return `<button type="button" aria-pressed="${this.answers[q.id] === o.v}" class="pf-opt${selected}" data-pf-option="${esc(o.v)}" data-pf-key="${i + 1}">
            ${icon}<span class="pf-opt__text"><span class="pf-opt__label">${esc(o.label)}</span>${desc}</span>
          </button>`;
        })
        .join('');
      const skip = q.skippable
        ? '<button type="button" class="pf-skip" data-pf-skip>Skip this question</button>'
        : '';
      render(
        this.mount,
        `<div class="pf-q" data-pf-q>
          <h2 class="pf-q__title">${esc(q.title)}</h2>
          ${q.sub ? `<p class="pf-q__sub">${esc(q.sub)}</p>` : ''}
          <div class="pf-q__options" role="group" aria-label="${esc(q.title)}">${opts}</div>
          ${skip}
        </div>`
      );
      requestAnimationFrame(() => this.mount.querySelector('[data-pf-q]')?.classList.add('is-in'));

      this.mount.querySelectorAll('[data-pf-option]').forEach((btn) => {
        btn.addEventListener('click', () => this.answer(q, btn.getAttribute('data-pf-option'), btn));
      });
      this.mount.querySelector('[data-pf-skip]')?.addEventListener('click', () => this.answer(q, null));
      this.focusStep();
    }

    // Phase C type-ahead: search the player's exact current racket from the in-stock pool.
    // Only distinct models are offered; a prominent skip handles "we don't carry yours".
    renderSearchQuestion(q) {
      // Build a deduped, distinct-model list. Avoid the "Adidas Adidas …" double-vendor when the
      // title already starts with the vendor name.
      const seen = new Set();
      const items = [];
      for (const p of this.products) {
        const key = modelKey(p);
        if (seen.has(key)) continue;
        seen.add(key);
        const title = (p.title || '').trim();
        const vendor = (p.vendor || '').trim();
        const label = vendor && !title.toLowerCase().startsWith(vendor.toLowerCase()) ? `${vendor} ${title}` : title;
        if (label) items.push({ handle: p.handle, label, hay: label.toLowerCase() });
      }
      items.sort((x, y) => x.label.localeCompare(y.label));
      const skip = q.skippable
        ? `<button type="button" class="pf-skip" data-pf-skip>${esc(q.skipLabel || 'Skip this question')}</button>`
        : '';
      render(
        this.mount,
        `<div class="pf-q" data-pf-q>
          <h2 class="pf-q__title">${esc(q.title)}</h2>
          ${q.sub ? `<p class="pf-q__sub">${esc(q.sub)}</p>` : ''}
          <div class="pf-search">
            <input type="text" class="pf-search__input" data-pf-search autocomplete="off" role="combobox"
              aria-expanded="false" aria-controls="pf-search-list" placeholder="Start typing a brand or model…" aria-label="${esc(q.title)}">
            <div class="pf-search__list" id="pf-search-list" data-pf-search-list role="listbox" hidden></div>
          </div>
          ${skip}
        </div>`
      );
      requestAnimationFrame(() => this.mount.querySelector('[data-pf-q]')?.classList.add('is-in'));
      const input = this.mount.querySelector('[data-pf-search]');
      const listEl = this.mount.querySelector('[data-pf-search-list]');
      const MAX = 6;
      const renderMatches = () => {
        const term = (input.value || '').trim().toLowerCase();
        if (!term) {
          listEl.hidden = true;
          listEl.replaceChildren();
          input.setAttribute('aria-expanded', 'false');
          return;
        }
        const matches = items.filter((it) => it.hay.includes(term)).slice(0, MAX);
        listEl.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        if (!matches.length) {
          render(listEl, '<p class="pf-search__empty">No match — skip below if we don’t carry yours.</p>');
          return;
        }
        render(
          listEl,
          matches.map((m) => `<button type="button" class="pf-search__opt" role="option" data-h="${esc(m.handle)}">${esc(m.label)}</button>`).join('')
        );
        listEl.querySelectorAll('[data-h]').forEach((b) => b.addEventListener('click', () => this.answer(q, b.getAttribute('data-h'))));
      };
      input?.addEventListener('input', renderMatches);
      input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const first = listEl.querySelector('[data-h]');
          if (first) this.answer(q, first.getAttribute('data-h'));
        }
      });
      this.mount.querySelector('[data-pf-skip]')?.addEventListener('click', () => this.answer(q, null));
      this.focusStep();
    }

    answer(q, value, btn) {
      if (this.locked) return;
      this.locked = true;
      if (btn) {
        this.mount.querySelectorAll('.pf-opt').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
      }
      if (value == null) delete this.answers[q.id];
      else this.answers[q.id] = value;
      // answering an upstream branch invalidates downstream branch answers
      if (q.id === 'gender' && value !== 'women') delete this.answers.color;
      // level is the master branch: beginners get prior_sport, experienced get the current-racket branch
      if (q.id === 'level') {
        if (value !== 'beginner') {
          delete this.answers.prior_sport;
        } else {
          delete this.answers.current_racket;
          delete this.answers.current_shape;
          delete this.answers.dislike;
        }
      }
      this.history.push(q.id);
      this.persist(false);
      this.track('pf_answer', { pf_question: q.id, pf_value: value || 'skipped' });
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const advance = () => {
        const flow = this.visibleQuestions();
        const next = flow[flow.indexOf(q) + 1];
        this.locked = false;
        if (next) {
          this.renderQuestion(next);
          this.persist(false);
        } else this.finish();
      };
      setTimeout(() => {
        const panel = this.mount.querySelector('[data-pf-q]');
        if (panel && !reducedMotion) {
          panel.classList.add('is-out');
          setTimeout(advance, 100);
        } else advance();
      }, reducedMotion ? 0 : 120);
    }

    back() {
      if (this.locked) return;
      const prevId = this.history.pop();
      if (!prevId) {
        this.show('intro');
        this.focusStep('[data-pf-start]');
        return;
      }
      const q = QUESTIONS.find((x) => x.id === prevId);
      this.show('quiz');
      this.renderQuestion(q || this.visibleQuestions()[0]);
      this.persist(false);
    }

    finish(instant) {
      this.persist(true);
      this.ranked = rank(this.products, this.answers, this.brandOpts);
      if (!instant) {
        this.track('pf_complete', { pf_top: this.ranked[0]?.p?.handle });
        this.beacon('quiz_completed', {
          answers: this.answers,
          matches: this.ranked.slice(0, 3).map((r) => ({ handle: r.p.handle, vid: r.p.vid, pct: r.pct })),
          engine_version: PF_ENGINE_VERSION,
        });
      }
      if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.show('results');
        this.renderResults();
        if (!instant) this.focusStep('.pf__results-title');
        return;
      }
      this.show('calc');
      const lines = this.querySelectorAll('[data-pf-calc-line]');
      lines.forEach((l, i) => setTimeout(() => l.classList.add('is-on'), i * 180));
      setTimeout(() => {
        this.show('results');
        this.renderResults();
        this.scrollToSelf();
        this.focusStep('.pf__results-title');
      }, 650);
    }

    renderResults(more) {
      const wrap = this.querySelector('[data-pf-results]');
      if (!wrap) return;
      const initial = Number(this.dataset.pfInitial || 3);
      this.visibleResults = more ? this.visibleResults + 3 : initial;
      const slice = this.ranked.slice(0, this.visibleResults);
      if (!slice.length) {
        render(
          wrap,
          '<p class="pf-empty">No rackets matched — the collection may be out of stock. <a href="/collections/padel-rackets">Browse all rackets</a>.</p>'
        );
        return;
      }
      const cardEls = slice
        .map((r, i) => {
          const p = r.p;
          const hero = i === 0 ? ' pf-card--hero' : '';
          const rankLabel = i === 0 ? 'your match' : `#${i + 1} match`;
          const onSale = Number(p.cap) > Number(p.price);
          const saveAmt = onSale ? Number(p.cap) - Number(p.price) : 0;
          const savePct = onSale && Number(p.cap) ? Math.round((saveAmt / Number(p.cap)) * 100) : 0;
          const compare = onSale ? `<s class="pf-card__compare">${fmt(p.cap)}</s>` : '';
          const saveBadge = onSale
            ? `<span class="pf-card__save">Save ${fmt(saveAmt)}${savePct ? ` · ${savePct}%` : ''}</span>`
            : '';
          const chips = (r.chips || []).map((cTxt) => `<span class="pf-chip">${esc(cTxt)}</span>`).join('');
          const flagEls =
            `${onSale ? '<span class="pf-card__flag pf-card__flag--sale">On Sale</span>' : ''}` +
            `${p.bs ? '<span class="pf-card__flag">Best seller</span>' : ''}`;
          const flags = flagEls ? `<div class="pf-card__flags">${flagEls}</div>` : '';
          return `<article class="pf-card${hero}" data-pf-handle="${esc(p.handle)}">
            <a class="pf-card__media" href="${esc(p.url)}" data-pf-view>
              ${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.title)}" loading="${i === 0 && !more ? 'eager' : 'lazy'}"${i === 0 && !more ? ' fetchpriority="high"' : ''} width="720" height="720">` : ''}
              <span class="pf-card__match"><strong>${r.pct}%</strong>&nbsp;${rankLabel}</span>
              ${flags}
            </a>
            <div class="pf-card__body">
              <p class="pf-card__vendor">${esc(p.vendor)}</p>
              <h3 class="pf-card__title"><a href="${esc(p.url)}" data-pf-view>${esc(p.title)}</a></h3>
              <div class="pf-card__chips">${chips}</div>
              <p class="pf-card__price">${fmt(p.price)} ${compare} ${saveBadge}</p>
              <div class="pf-card__actions">
                <button type="button" class="button pf-btn pf-btn--primary" data-pf-atc="${esc(p.vid)}" data-pf-pct="${r.pct}" data-pf-rank="${i + 1}">Add to cart</button>
                <a class="button-secondary pf-btn pf-btn--ghost" href="${esc(p.url)}" data-pf-view>View racket</a>
              </div>
            </div>
          </article>`;
        });
      // the free-gift offer sits as a full-width row right under the #1 match
      const offer = this.offerBlock();
      if (offer && cardEls.length) cardEls.splice(1, 0, offer);
      render(wrap, cardEls.join(''));

      wrap.querySelectorAll('[data-pf-view]').forEach((a) =>
        a.addEventListener('click', () => {
          const handle = a.closest('[data-pf-handle]')?.getAttribute('data-pf-handle');
          this.track('pf_result_click', { pf_handle: handle });
          this.beacon('quiz_result_clicked', { handle });
        })
      );
      wrap.querySelectorAll('[data-pf-atc]').forEach((btn) => btn.addEventListener('click', () => this.addToCart(btn)));
      const offerForm = wrap.querySelector('[data-pf-offer-form]');
      if (offerForm) offerForm.addEventListener('submit', (event) => this.submitOffer(event, offerForm));
      const moreBtn = this.querySelector('[data-pf-more]');
      if (moreBtn) moreBtn.hidden = this.visibleResults >= this.ranked.length;
    }

    async addToCart(btn) {
      const id = Number(btn.getAttribute('data-pf-atc'));
      if (!id || btn.disabled) return;
      const original = btn.textContent;
      const pct = btn.getAttribute('data-pf-pct') || '';
      const rank = btn.getAttribute('data-pf-rank') || '';
      const handle = btn.closest('[data-pf-handle]')?.getAttribute('data-pf-handle') || '';
      this.beacon('quiz_add_to_cart', { handle, vid: id, pct: Number(pct) || null, rank: Number(rank) || null });
      // Open the kit-upsell popup INSTANTLY (optimistic) — like a PDP add — instead of waiting for
      // the add + cart round-trips. We feed it a synthetic cart built from the card so it opens
      // immediately; notifyCartAdd's real cart:update afterwards refreshes the header count.
      const overlay = this.closest('product-finder-dialog');
      const entry = this.ranked.find((r) => Number(r.p.vid) === id);
      btn.closest('.pf-card__body')?.querySelector('[data-pf-cart-error]')?.remove();
      if (entry && !overlay) {
        try {
          const cents = Math.round(Number(entry.p.price) * 100) || 0;
          const line = {
            variant_id: id,
            key: String(id),
            product_title: entry.p.title,
            title: entry.p.title,
            product_type: 'Padel Racket',
            tags: ['racket'],
            image: entry.p.img || null,
            price: cents,
            line_price: cents,
            final_line_price: cents,
            quantity: 1,
            options_with_values: [],
          };
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: { item_count: 1, total_price: cents, items: [line] },
                sourceId: 'product-finder',
                data: { source: 'product-form-component', variantId: String(id), itemCount: 1 },
              },
            })
          );
        } catch (e) {
          /* non-fatal — notifyCartAdd still opens it after the add lands */
        }
      }
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        // Hidden (_-prefixed) line-item properties: persist to the ORDER and show in the admin
        // line item, but stay invisible to the customer at checkout. This is how a quiz purchase
        // stays attributable in orders + analytics/exports/webhooks.
        const properties = { _pf_session: this.sessionId };
        if (this.attributionLabel) {
          properties._source = pct ? `${this.attributionLabel} · ${pct}% match · #${rank}` : this.attributionLabel;
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1, properties }] }),
        });
        if (!res.ok) throw new Error(`cart/add ${res.status}`);
        const addData = await res.json().catch(() => null); // the added line(s) — popup fallback
        btn.textContent = 'Added ✓';
        btn.classList.add('is-added');
        this.track('pf_add_to_cart', {
          pf_variant: id,
          pf_handle: handle,
          pf_match: Number(pct) || undefined,
          pf_rank: Number(rank) || undefined,
          pf_session: this.sessionId,
        });
        if (overlay && document.querySelector('kit-upsell-dialog')) await overlay.close();
        this.notifyCartAdd(id, addData); // fire the theme cart-add flow → kit-upsell popup + header count
        const actions = btn.closest('.pf-card__actions');
        if (actions && !actions.querySelector('[data-pf-viewcart]')) {
          const link = document.createElement('a');
          link.href = '/cart';
          link.className = 'pf-skip';
          link.setAttribute('data-pf-viewcart', '');
          link.textContent = 'View cart →';
          actions.appendChild(link);
        }
      } catch (e) {
        btn.disabled = false;
        btn.textContent = original;
        const error = document.createElement('p');
        error.className = 'pf__cart-error';
        error.dataset.pfCartError = '';
        error.setAttribute('role', 'alert');
        error.textContent = 'This racket couldn’t be added. Please try again or view the racket for availability.';
        btn.closest('.pf-card__body')?.append(error);
        // Add failed — close the optimistically-opened popup so we don't show a phantom add.
        try {
          document.querySelector('kit-upsell-dialog')?.closeDialog?.();
        } catch (_) {
          /* ignore */
        }
      }
    }

    // Make a quiz add behave exactly like a PDP add: dispatch the theme's CartAddEvent
    // ('cart:update') that the <kit-upsell-dialog> popup AND the header cart icon listen for.
    // The popup gate needs data.source to include 'product-form' + the variantId; rackets pass
    // its #isAnchor check. We pass the fresh cart as `resource` so the popup opens immediately
    // (it falls back to GET /cart.js if absent). No-op if no listener is present.
    async notifyCartAdd(variantId, addData) {
      let cart = null;
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (r.ok) cart = await r.json();
      } catch (e) {
        /* fall back to the add response below */
      }
      // If /cart.js is blocked or non-JSON (e.g. a CDN/bot challenge on a preview theme), use the
      // /cart/add.js response — it carries the added line, which is all the popup needs to open.
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        const items = addData && (Array.isArray(addData.items) ? addData.items : addData.id ? [addData] : null);
        if (items && items.length) cart = { items, item_count: null, total_price: null };
      }
      try {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'product-finder',
              data: { source: 'product-form-component', variantId: String(variantId), itemCount: 1 },
            },
          })
        );
      } catch (e) {
        /* non-fatal — the inline "Added ✓ / View cart" feedback already shows */
      }
    }

    // Bold free-gift offer, rendered as a full-width row directly under the #1 match.
    // MAP-safe: it never discounts the racket — the incentive is a gift-with-purchase,
    // so all copy is driven by section settings (data-pf-offer-*) and editable.
    offerBlock() {
      const d = this.dataset;
      if (d.pfOffer !== 'on') return '';
      const eyebrow = d.pfOfferEyebrow || 'Free gift with your racket';
      const heading = d.pfOfferHeading || 'Your match comes with a free gift';
      const sub =
        d.pfOfferSub ||
        'Order your match and we’ll throw in a free welcome gift — on us.';
      const button = d.pfOfferButton || 'Claim my free gift';
      const fine = d.pfOfferFine || '';
      const done = d.pfOfferDone || 'Your free gift is on its way to your inbox.';
      const gift =
        '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4"/><rect x="5" y="12" width="14" height="9"/><line x1="12" y1="8" x2="12" y2="21"/><path d="M12 8c-2 0-4.5-1-4.5-3C7.5 3.5 9 3 10 3c2 0 2 3 2 5zM12 8c2 0 4.5-1 4.5-3C16.5 3.5 15 3 14 3c-2 0-2 3-2 5z"/></svg>';
      return `<aside class="pf__offer${this.offerDone ? ' is-done' : ''}" data-pf-offer-block>
        <span class="pf__offer-aura" aria-hidden="true"></span>
        <div class="pf__offer-text">
          <span class="pf__offer-eyebrow">${gift}<span>${esc(eyebrow)}</span></span>
          <h3 class="pf__offer-title">${esc(heading)}</h3>
          <p class="pf__offer-sub">${esc(sub)}</p>
        </div>
        <form class="pf__offer-form" data-pf-offer-form novalidate>
          <input type="email" name="email" autocomplete="email" inputmode="email" placeholder="you@email.com" aria-label="Email address" required>
          <button type="submit" class="button pf-btn pf-btn--gift">${esc(button)}</button>
        </form>
        <div class="pf__offer-success" role="status" aria-live="polite">
          <span class="pf__offer-check" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
          <span class="pf__offer-success-text"><strong>Check your inbox</strong><span>${esc(done)}</span></span>
        </div>
        ${fine ? `<p class="pf__offer-fine">${esc(fine)}</p>` : ''}
      </aside>`;
    }

    async submitOffer(event, form) {
      event.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button');
      const email = (input?.value || '').trim();
      if (!email || !email.includes('@')) {
        input?.focus();
        return;
      }
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Sending…';
      try {
        await this.subscribeEmail(email);
        this.offerDone = true;
        this.track('pf_email_capture');
        this.beacon('quiz_email_captured', { top_handle: this.ranked[0]?.p?.handle });
        form.closest('[data-pf-offer-block]')?.classList.add('is-done');
      } catch (e) {
        button.disabled = false;
        button.textContent = original;
      }
    }

    async subscribeEmail(email) {
      const company = this.dataset.pfKlaviyoCompany;
      const list = this.dataset.pfKlaviyoList;
      if (!company || !list) return; // unconfigured → UX still completes, just no subscribe
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Racket Finder Quiz',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: {
                        // Quiz answers + the top match, so the Klaviyo welcome flow can personalise
                        // the gift email ("Your match: …") and segment by level/style/budget.
                        quiz: 'racket-finder',
                        quiz_top_match: this.ranked[0]?.p?.handle || '',
                        // variant id powers the email's one-click cart permalink
                        // (/cart/{vid}:1,{ballVid}:1?discount=RACKETGIFT)
                        quiz_top_match_vid: this.ranked[0]?.p?.vid || '',
                        quiz_top_match_title: this.ranked[0]?.p?.title || '',
                        quiz_top_match_url: this.ranked[0]?.p?.url
                          ? `${location.origin}${this.ranked[0].p.url}`
                          : '',
                        // absolute https — the pool img is protocol-relative (//host/…), which
                        // renders on the https storefront but breaks in email clients.
                        quiz_top_match_image: (this.ranked[0]?.p?.img || '').replace(/^\/\//, 'https://'),
                        quiz_match_pct: this.ranked[0]?.pct || '',
                        quiz_level: this.answers.level || '',
                        quiz_style: this.answers.style || '',
                        quiz_budget: this.answers.budget || '',
                      },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        }
      );
      if (!res.ok && res.status !== 202) throw new Error(`klaviyo ${res.status}`);
    }

    retake() {
      try {
        sessionStorage.removeItem('pf-state-v1');
      } catch (e) {
        /* ignore */
      }
      this.visibleResults = 0;
      this.answers = {};
      this.history = [];
      this.current = null;
      this.show('intro');
      this.scrollToSelf();
      this.track('pf_retake');
    }
  }

  if (!customElements.get('product-finder-app')) {
    customElements.define('product-finder-app', ProductFinderApp);
  }

  // theme editor: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll?.('product-finder-app').forEach((el) => {
      delete el.dataset.pfInit;
      el.connectedCallback?.();
    });
  });
})();
